import React from "react";
import { capture } from "../../../analytics";

// Last line of defense against a blank page. Without a boundary, any
// uncaught render error unmounts the whole React tree and the user
// sees an empty cream screen with no way out except closing the tab.
// This shows what happened and offers the two exits that actually
// work: reload, or sign out (clear every token store) and reload.

const clearAuthStores = () => {
  try {
    ["cognitoIdToken", "cognitoRefreshToken", "userProfileInfo"].forEach((k) =>
      sessionStorage.removeItem(k)
    );
    ["auth_token", "refresh_token", "user_info", "user_role"].forEach((k) =>
      localStorage.removeItem(k)
    );
  } catch (e) {
    // ignore
  }
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("Render error caught by ErrorBoundary:", error, info);
    capture("render_error", {
      message: String((error && error.message) || error).slice(0, 300),
      path: typeof window !== "undefined" ? window.location.pathname : "",
    });
  }

  render() {
    if (!this.state.error) return this.props.children;
    const message = String(
      (this.state.error && this.state.error.message) || this.state.error
    ).slice(0, 300);
    return (
      <div style={{ maxWidth: 520, margin: "60px auto", padding: 16, textAlign: "center", fontFamily: "inherit" }}>
        <h2 style={{ fontSize: 22, marginBottom: 8 }}>Something went wrong</h2>
        <p style={{ color: "#5b667a", fontSize: 14, marginBottom: 18 }}>
          The page hit an error it couldn't recover from. Reloading usually
          fixes it; if it keeps happening, sign out and back in.
        </p>
        <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{ padding: "10px 18px", background: "#1f7a3a", color: "#fff", border: "none", borderRadius: 6, fontWeight: 700, cursor: "pointer" }}
          >
            Reload
          </button>
          <button
            type="button"
            onClick={() => {
              clearAuthStores();
              window.location.href = "/login";
            }}
            style={{ padding: "10px 18px", background: "#fff", color: "#1f6feb", border: "1px solid #1f6feb", borderRadius: 6, fontWeight: 700, cursor: "pointer" }}
          >
            Sign out and reload
          </button>
        </div>
        <details style={{ marginTop: 18, textAlign: "left", color: "#8b94a6", fontSize: 12 }}>
          <summary>Technical details</summary>
          <code style={{ wordBreak: "break-word" }}>{message}</code>
        </details>
      </div>
    );
  }
}

export default ErrorBoundary;
